import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { makeFormData } from '../../helpers/formData'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import gridView from '@/global/components/grid/GridView.vue'
import readoutsGridBlock from './ReadoutsGridBlock.vue'

export default {
  name: 'Readouts',

  data () {
    return {
      showReadoutsTable: true,
      showReadoutsDetails: false
    }
  },

  computed: {
    readoutsConfig () {
      return {
        title: this.$t('fleet/vehicle.readouts'),
        key: 'readouts',
        onTabChange: () => this.onReadoutsTabChange(),
        tabContent: [
          {
            component: this.tableGridComponent,
            visible: this.showReadoutsTable,
            apiConfig: {
              get: {
                module: 'fleet',
                route: 'vehicle-readout',
                method: 'get',
                queryParams: {
                  vehicle_id: this.$route.params.id
                }
              }
            },
            mobileConfig: {
              title: this.$t('fleet/vehicle.readouts')
            },
            title_and_back_config: {
              title: this.$t('fleet/vehicle.readouts')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewReadout()
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: readoutsGridBlock
              },
              useBorder: false
            },
            tableConfig: {
              showRowPointer: false,
              cellsConfig: {
                datetime: {
                  custom_display_value: (item) => {
                    return item?.datetime ? dateService.formatFromUtc(item.datetime, FORMATS.datetimeShort) : ''
                  }
                },
                type: {
                  custom_display_value: (item) => {
                    return item?.type?.name || ''
                  }
                }
              },
              headers: [
                {
                  label: this.$t('fleet/vehicle.readout_datetime'),
                  key: 'datetime',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle.readout_type'),
                  key: 'type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle.readout_value'),
                  key: 'value',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle.readout_author'),
                  key: 'user',
                  always_visible: true
                }
              ],
              paginationConfig: {
                perPageOptions: [20, 50, 100],
                perPage: 20
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'datetime',
                    label: this.$t('fleet/vehicle.readout_datetime'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.datetimeShort,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.datetimeShort, true)) {
                        updateValidationErrors({
                          datetime: this.$t('base.invalid_date').replace(':format', FORMATS.datetimeShort)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'type',
                    label: this.$t('fleet/vehicle.readout_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-readout/types',
                      custom_display_value: (item) => {
                        return item?.name || ''
                      }
                    }
                  },
                  {
                    key: 'value',
                    label: this.$t('fleet/vehicle.readout_value'),
                    field_type: 'number'
                  }
                ]
              }
            }
          },
          {
            component: this.tabActionsComponent,
            visible: this.showReadoutsDetails,
            title: this.$t('fleet/activity.vehicle_readouts'),
            showEditButtons: true,
            showBackButton: {
              onGoBack: () => this.exitFromReadoutsDetails()
            },
            onCancelTabSave: () => this.exitFromReadoutsDetails(true),
            onSaveClick: () => this.onSaveReadouts()
          },
          {
            component: this.columnsContentComponent,
            visible: this.showReadoutsDetails,
            showBackButton: {
              useChangesNotSavedDialog: false
            },
            key: 'readoutsDetails',
            rows: [
              {
                fields: [
                  {
                    key: 'type_id',
                    label: this.$t('fleet/vehicle.readout_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicles/readout-types',
                      custom_display_value: (item) => {
                        return item.name
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'value',
                    label: this.$t('fleet/vehicle.readout_value'),
                    hint: this.$t('fleet/vehicle.readout_hint'),
                    visible: true,
                    editable: true
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onNewReadout () {
      const { setCreateMode, clearData } = registeredTabsHandlers?.readouts || {}
      if (typeof setCreateMode === 'function') {
        setCreateMode(true)
      }
      if (typeof clearData === 'function') {
        clearData()
      }
      this.showReadoutsTable = false
      this.showReadoutsDetails = true
    },

    exitFromReadoutsDetails (canceling = false) {
      const { clearData, setCreateMode, setEditMode, getCreateModeState, cancelSave } = registeredTabsHandlers?.readouts || {}
      let isCreateMode = false
      if (typeof getCreateModeState === 'function') {
        isCreateMode = getCreateModeState()
      }

      if (isCreateMode) {
        this.showReadoutsTable = true
        this.showReadoutsDetails = false

        if (typeof clearData === 'function') {
          clearData()
        }
        if (typeof setCreateMode === 'function') {
          setCreateMode(false)
        }
        if (typeof setEditMode === 'function') {
          setEditMode(false)
        }
      }
      else if (typeof cancelSave === 'function') {
        if (!canceling) {
          this.showReadoutsTable = true
          this.showReadoutsDetails = false
        }

        cancelSave()
      }
    },

    async onSaveReadouts () {
      try {
        const { getFormData } = registeredTabsHandlers?.readouts || {}
        const { fetchData } = registeredTableHandlers?.readouts || {}
        const { id: vehicleId } = this.$route?.params || {}

        if (!vehicleId) return

        const data = typeof getFormData === 'function' ? getFormData()?.data : {}
        const formData = makeFormData(data)
        formData.append('vehicle_id', vehicleId)

        await api('fleet').post('vehicle-readout', formData)
        this.exitFromReadoutsDetails()

        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        console.error()
      }
    },

    onReadoutsTabChange () {
      this.showMainEditButtons = false
    }
  }
}
